/**
 * Map domain rows to hosting AWS account rows for grouping and counts.
 * Keep in sync with Domains page — company tenant ids must not be used as group keys.
 */

/**
 * True if `raw` is an aws-accounts row id (12-digit AWS id) present in hostingAccounts.
 * Company tenant ids (e.g. company_staging_001) must NOT be used for per-account UI grouping.
 */
export function isHostingAccountRowId(raw, hostingAccounts) {
  if (raw === undefined || raw === null) return false;
  const s = String(raw).trim();
  if (!s) return false;
  if (s.startsWith('company_')) return false;
  return hostingAccounts.some((a) => String(a.id) === s);
}

/**
 * Resolve a domain to exactly one hosting account id for Domains table grouping (AWS account, not tenant).
 */
export function resolveHostingAccountIdForDomain(domain, hostingAccounts) {
  if (!Array.isArray(hostingAccounts) || hostingAccounts.length === 0) return '';
  const raw = domain && domain.accountId;
  if (isHostingAccountRowId(raw, hostingAccounts)) {
    return String(raw).trim();
  }
  const dn = String(domain.domain || '')
    .toLowerCase()
    .trim();
  if (!dn) return String(hostingAccounts[0].id);
  let bestId = String(hostingAccounts[0].id);
  let bestScore = -1;
  for (const acc of hostingAccounts) {
    const names = Array.isArray(acc.domains) ? acc.domains : [];
    for (const ad of names) {
      const al = String(ad).toLowerCase().trim();
      if (!al) continue;
      let score = 0;
      if (dn === al) score = 1000 + al.length;
      else if (dn.endsWith(`.${al}`)) score = 500 + al.length;
      else {
        const rootDn = dn.split('.').slice(-2).join('.');
        const rootAl = al.split('.').slice(-2).join('.');
        if (rootDn && rootDn === rootAl) score = 100 + al.length;
      }
      if (score > bestScore) {
        bestScore = score;
        bestId = String(acc.id);
      }
    }
  }
  return bestScore >= 0 ? bestId : String(hostingAccounts[0].id);
}

/**
 * Count domains per hosting account id using the same resolver as the Domains page.
 * @param {Array<object>} domainRows - from GET /domains `data.domains`
 * @param {Array<object>} hostingAccounts - accountsForGrouping list
 * @returns {Record<string, number>}
 */
export function countDomainsPerHostingAccount(domainRows, hostingAccounts) {
  const counts = {};
  if (!Array.isArray(hostingAccounts)) return counts;
  for (const a of hostingAccounts) {
    counts[String(a.id)] = 0;
  }
  if (!Array.isArray(domainRows) || hostingAccounts.length === 0) return counts;
  for (const domain of domainRows) {
    const hid = resolveHostingAccountIdForDomain(domain, hostingAccounts);
    if (Object.prototype.hasOwnProperty.call(counts, hid)) {
      counts[hid] += 1;
    }
  }
  return counts;
}
